import { AbstractDestroyable, assert, Burden, DestroyableScheduler, lazy, Type, VoidCallback } from '@deqo/foundation';
import { EventRouter, EventSink, EventSource } from '@deqo/podopolis';
import type { interfaces } from 'inversify';
import { Container, inject, injectable } from 'inversify';
import React, { useContext } from 'react';

/**
 * Defines a React Context that provides subtree components access to scoped Container.
 */
export const ContainerScope = React.createContext<Container>(null!);

/**
 * Defines a symbol that can be used to represent a registered Container service.
 *
 * @param name the name of the service
 */
export const ServiceSymbol = <T>(name: string) => Symbol(name) as unknown as interfaces.Newable<T>;

/**
 * React Hook for resolving a dependency from the nearest scoped Container via the a ContainerScope context.
 *
 * @param identifier the service identifier
 */
export function useResolve<T>(identifier: interfaces.ServiceIdentifier<T>): T {
    const container = useContext(ContainerScope);
    return container.get(identifier);
}

/**
 * React Hook for optionally resolving a dependency from the nearest scoped Container via the a ContainerScope context.
 *
 * @param identifier the service identifier
 */
export function useResolveMaybe<T>(identifier: interfaces.ServiceIdentifier<T>): T | null {
    const container = useContext(ContainerScope);
    return container.isBound(identifier) ? container.get(identifier) : null;
}

/**
 * Alternative annotation to the _inject_ annotation provided by inversify.  This annotation supports both React class components
 * as well as regular service classes.  In the case of components, the annotation overrides the getter to directly resolve from
 * the nearest ContainerScope.  In the case of services, the annotation falls back to the behavior defined by _inject_.
 *
 * @param identifier
 */
export function resolve<T>(identifier: interfaces.ServiceIdentifier<T>): any {
    return (target: any, propertyName: string, original?: PropertyDescriptor) => {

        if (target?.constructor?.contextType === ContainerScope) {
            const descriptor = {
                enumerable: true,
                configurable: true,
                get: createResolveGetter(identifier),
            };
            if (original) {
                return descriptor;
            }
            else {
                Object.defineProperty(target, propertyName, descriptor);
            }
        }
        else {
            assert(!(target instanceof React.Component), '@resolve on a React component without the ContainerScope.');
            inject(identifier)(target, propertyName);
        }

    };
}

const createResolveGetter = <T>(identifier: interfaces.ServiceIdentifier<T>) => {
    return function (this: any) {
        assert(this.context, '@resolve on a React component where the context is null?');
        return this.context.get(identifier);
    };
};

/**
 * An Activator is a derivative service of a Container that can "activate" a given service type.  Activating means to resolve any annotated
 * dependencies the service type defines and returning a new instance of the type.  The type does not need to be registered with the
 * Container and will not follow any of the binding rules if it is registered.  Think of this as an alterative to "new".
 */
export abstract class Activator {

    public static createFor = (container: interfaces.Container) => new DefaultActivator(container);

    /**
     * Activates the specified type.
     *
     * @param type the type.
     */
    public abstract call<T>(type: interfaces.Newable<T>): T;
}

@injectable()
class DefaultActivator extends AbstractDestroyable implements Activator {

    constructor(private container: interfaces.Container) {
        super();
    }

    public readonly call = <T>(type: Type<T>): T => {
        this.guardDestroyed();
        return this.container.resolve(type);
    };
}

/**
 * A Resolver is a derivative service of a Container that can "resolve" a given service type.  Resolving is the same as fetching from the
 * Container directly, just through a most abstract interface.  Therefore, any type passed to a Resolver must have binding a Container
 * within the Container tree the Resolver is derived from.  Think of this as an alterative to "container.get".
 */
export abstract class Resolver {

    public static createFor = (container: interfaces.Container) => new DefaultResolver(container);

    /**
     * Resolves the specified service.
     *
     * @param identifier the service identifier.
     * @param strict whether to throw an exception on not found, defaults to true.
     */
    public abstract call<T>(identifier: interfaces.ServiceIdentifier<T>, strict?: boolean): T;
}

@injectable()
class DefaultResolver extends AbstractDestroyable implements Resolver {

    constructor(private container: interfaces.Container) {
        super();
    }

    public readonly call = <T>(identifier: interfaces.Newable<T>, strict = true): T => {
        this.guardDestroyed();
        if (!strict && !this.container.isBound(identifier)) return null;
        return this.container.get(identifier);
    };
}

@injectable()
export abstract class Controller extends AbstractDestroyable {

    public abstract start(): void;
}

@injectable()
export abstract class ActiveObject extends AbstractDestroyable {

    @lazy()
    protected get scheduler() {
        const ls = new DestroyableScheduler();
        this.burden(ls);
        return ls;
    }

    public abstract start(): void | Promise<void>;
}

@injectable()
export abstract class ActiveObjectWithEvents<T> extends ActiveObject implements EventSource<T> {

    protected readonly events = new EventRouter<T>();

    constructor() {
        super();
        this.burden(this.events);
    }

    public on(type: '*', to: EventSink<string[], string>): VoidCallback;
    public on<K extends keyof T>(type: K, to: EventSink<T[K], K>): VoidCallback;
    public on(type: any, to: any) {
        return this.events.on(type, to);
    }

    public once(type: '*', to: EventSink<string[], string>): VoidCallback;
    public once<K extends keyof T>(type: K, to: EventSink<T[K], K>): VoidCallback;
    public once(type: any, to: any) {
        return this.events.once(type, to);
    }
}

export type Loader = () => Promise<void>;

export type Connector = (r: Resolver) => void | Burden[] | Promise<void | Burden[]>;
export const Connector = (c: Connector) => c;

export type Startable = interfaces.ServiceIdentifier<ActiveObject> | interfaces.Newable<ActiveObject> | ActiveObject;

export type ServiceLifestyle = 'singleton' | 'transient';
export type ServiceTrait = 'startable';

export interface PackageHost {

    load(...loaders: Loader[]): this;
    plug(...connectors: Connector[]): this;
    register<T>(type: interfaces.ServiceIdentifier<T>, instance: T): this;
    register<T>(type: interfaces.ServiceIdentifier<T>, lifestyle: ServiceLifestyle, ...traits: ServiceTrait[]): this;
    start(...typesOrInstances: Startable[]): this;
}

export interface Package {
    install(host: PackageHost): PackageHost | void;
}

export type PackageType = Type<Package>;

export class DefaultPackageHost extends AbstractDestroyable implements PackageHost {

    private loaders = [] as Loader[];
    private connectors = [] as Connector[];
    private startables = [] as Startable[];

    constructor(private readonly container: Container) {
        super();
    }

    public load(...loaders: Loader[]): this {
        this.loaders.push(...loaders);
        return this;
    }

    public plug(...connectors: Connector[]): this {
        this.connectors.push(...connectors);
        return this;
    }

    public register<T>(type: interfaces.ServiceIdentifier<T>, lifestyle: ServiceLifestyle, ...traits: ServiceTrait[]): this;
    public register<T>(type: interfaces.ServiceIdentifier<T>, instance: T): this;
    public register(type: any, loi: any, ...traits: ServiceTrait[]): this {
        const lifestyle = (typeof (loi) === 'string' ? loi : null) as ServiceLifestyle;
        const instance = !!lifestyle ? null : loi;

        if (instance) {
            this.container.bind(type).toConstantValue(loi);
        }
        else {
            if (lifestyle === 'singleton') {
                this.container.bind(type).toSelf().inSingletonScope();
            }
            else if (lifestyle === 'transient') {
                this.container.bind(type).toSelf().inTransientScope();
            }
            else {
                assert(false, `register: invalid lifestyle ${lifestyle}`);
            }
            if (traits.includes('startable')) {
                this.startables.push(type);
            }
        }

        return this;
    }

    public start(...typesOrInstances: Startable[]): this {
        this.startables.push(...typesOrInstances);
        return this;
    }

    public async go() {
        const resolver = this.container.get(Resolver);
        for (const c of this.connectors) {
            let r = c(resolver) as any;
            if (!!r && 'then' in r) {
                r = await r;
            }
            if (!!r && Array.isArray(r)) {
                this.burden(...r);
            }
        }
        for (const l of this.loaders) {
            await l();
        }
        for (const s of this.startables) {
            const ao = this.resolveStartable(s);
            const maybePromise = ao.start();
            if (!!maybePromise) await maybePromise;
            this.burden(ao);
        }
    }

    private resolveStartable(s: Startable): ActiveObject {
        if (s instanceof ActiveObject) {
            return s;
        }
        else if (this.container.isBound(s)) {
            return this.container.get(s);
        }
        else {
            return this.container.resolve(s as any);
        }
    }
}

@injectable()
export class Scheduler extends AbstractDestroyable {

    private timeouts = new Set<any>();

    constructor() {
        super();
        this.burden(() => this.timeouts.forEach(clearTimeout));
    }

    public invokeImmedate<T extends (...args: any[]) => any>(func: T) {
        const t = setTimeout(() => {
            this.timeouts.delete(t);
            func();
        }, 0);
        this.timeouts.add(t);
        return () => {
            clearTimeout(t);
            this.timeouts.delete(t);
        };
    }

    public invokeAfter<T extends (...args: any[]) => any>(ms: number, func: T) {
        const t = setTimeout(() => {
            this.timeouts.delete(t);
            func();
        }, ms);
        this.timeouts.add(t);
        return () => {
            clearTimeout(t);
            this.timeouts.delete(t);
        };
    }
}
